import { createContext, useContext, useState } from "react";

const branches = [
  { id: 1, name: "Adalat Bazaar" },
  { id: 2, name: "Mohali Phase 7" },
];

const InventoryContext = createContext();

export function BranchInventoryProvider({ children }) {
  const [inventory, setInventory] = useState([
    {
      id: 1,
      name: "Milk",
      stock: 120,
      unit: "Litres",
      minStock: 20,
      type: "raw",
      branchId: 1,
    },
    {
      id: 2,
      name: "Sugar",
      stock: 80,
      unit: "Kg",
      minStock: 15,
      type: "raw",
      branchId: 1,
    },
    {
      id: 3,
      name: "Paneer",
      stock: 30,
      unit: "Kg",
      minStock: 10,
      type: "raw",
      branchId: 2,
    },
  ]);

  const [ledger, setLedger] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [activeBranch, setActiveBranch] = useState(branches[0]);
  const branchInventory = inventory.filter(
    (i) => i.branchId === activeBranch.id
  );

  const addInward = (entry) => {
    setInventory((prev) =>
      prev.map((item) =>
        item.name === entry.item && item.branchId === activeBranch.id
          ? { ...item, stock: item.stock + Number(entry.qty) }
          : item
      )
    );

    setLedger((prev) => [
      ...prev,
      { ...entry, type: "inward", branchId: activeBranch.id },
    ]);
  };

  const addOutward = (entry) => {
    const target = branchInventory.find((i) => i.name === entry.item);

    if (!target || target.stock < entry.qty) {
      alert("Insufficient stock!");
      return false;
    }

    setInventory((prev) =>
      prev.map((item) =>
        item.name === entry.item && item.branchId === activeBranch.id
          ? { ...item, stock: item.stock - Number(entry.qty) }
          : item
      )
    );

    setLedger((prev) => [
      ...prev,
      { ...entry, type: "outward", branchId: activeBranch.id },
    ]);

    return true;
  };

  const addNewItem = (item) => {
    setInventory((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: item.name,
        stock: Number(item.stock || 0),
        unit: item.unit || "Kg",
        minStock: Number(item.minStock || 0),
        category: item.category,
        type: item.type,
        branchId: activeBranch.id,
      },
    ]);
  };

  const addRecipe = (recipe) => {
    setRecipes((prev) => [...prev, recipe]);
  };

  const createBatch = ({ product, quantity, unit = "Kg", location }) => {
    const recipe = recipes.find((r) => r.product === product);
    if (!recipe) {
      alert("No recipe found!");
      return false;
    }

    for (let ing of recipe.ingredients) {
      const stockItem = branchInventory.find((i) => i.name === ing.item);
      const requiredQty = ing.qty * quantity;

      if (!stockItem || stockItem.stock < requiredQty) {
        alert(`Insufficient stock for ${ing.item}`);
        return false;
      }
    }

    recipe.ingredients.forEach((ing) => {
      const requiredQty = ing.qty * quantity;

      setInventory((prev) =>
        prev.map((item) =>
          item.name === ing.item && item.branchId === activeBranch.id
            ? { ...item, stock: item.stock - requiredQty }
            : item
        )
      );
    });

    const batch = {
      id: "B-" + Math.floor(1000 + Math.random() * 9000),
      product,
      quantity,
      unit,
      location,
      status: "Running",
      date: new Date().toISOString().split("T")[0],
      branchId: activeBranch.id,
    };

    setBatches((prev) => [batch, ...prev]);
    return true;
  };

  const completeBatch = (batchId) => {
    const batch = batches.find(
      (b) => b.id === batchId && b.branchId === activeBranch.id
    );
    if (!batch) return;

    const existing = inventory.find(
      (i) => i.name === batch.product && i.branchId === activeBranch.id
    );

    if (existing) {
      setInventory((prev) =>
        prev.map((i) =>
          i.name === batch.product && i.branchId === activeBranch.id
            ? { ...i, stock: i.stock + Number(batch.quantity) }
            : i
        )
      );
    } else {
      setInventory((prev) => [
        ...prev,
        {
          id: Date.now(),
          name: batch.product,
          stock: Number(batch.quantity),
          unit: batch.unit,
          minStock: 0,
          type: "finished",
          branchId: activeBranch.id,
        },
      ]);
    }

    setBatches((prev) =>
      prev.map((b) => (b.id === batchId ? { ...b, status: "Completed" } : b))
    );
  };

  const lowStockAlerts = branchInventory
    .filter((i) => i.stock < i.minStock)
    .map((i) => ({
      id: i.id,
      item: i.name,
      stock: i.stock,
      min: i.minStock,
      locationType: "Factory",
      locationName: activeBranch.name,
      type: i.type,
      branchId: activeBranch.id,
    }));

  const productionAlerts = batches.filter(
    (b) => b.status === "Running" && b.branchId === activeBranch.id
  );

  return (
    <InventoryContext.Provider
      value={{
        branches,
        activeBranch,
        setActiveBranch,
        inventory: branchInventory,
        ledger,
        recipes,
        batches,
        addInward,
        addOutward,
        addNewItem,
        addRecipe,
        createBatch,
        completeBatch,
        lowStockAlerts,
        productionAlerts,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export const useInventory = () => useContext(InventoryContext);
